export type LogLevel = "trace" | "debug" | "info" | "warn" | "error" | "fatal";

export type LogAttributes = Record<string, unknown>;

export interface NormalizedLogRecord {
  body: string;
  level: LogLevel;
  attributes: Record<string, string | number | boolean>;
}

const REDACTED = "[redacted]";
const MAX_BODY_LENGTH = 300;
const MAX_STRING_LENGTH = 1000;
const MAX_STACK_LENGTH = 4000;
const MAX_ATTRIBUTE_COUNT = 40;

const sensitiveKeyPattern =
  /(^|[_.-])(password|passcode|secret|token|authorization|cookie|api[_-]?key|private[_-]?key|credential|signature|otp|verification[_-]?code|email|phone|phone[_-]?number|contact|address|card|cvv|pan|latitude|longitude|lat|lng|lon|coordinates|coordinate|location|gps[_-]?point|route[_-]?points)([_.-]|$)/i;

const allowedIdentityKeys = new Set([
  "distinctId",
  "distinct_id",
  "posthogDistinctId",
  "posthog_distinct_id",
  "sessionId",
  "session_id",
  "userId",
  "user_id",
  "profileId",
  "profile_id",
]);

export function normalizeLogBody(body: unknown, fallback: string): string {
  const raw =
    typeof body === "string"
      ? body
      : body instanceof Error
        ? body.message
        : fallback;

  return (
    truncate(redactString(raw.trim().replace(/\s+/g, " ")), MAX_BODY_LENGTH) ||
    fallback
  );
}

export function normalizeLogEvent(body: string): string {
  const normalized = body
    .toLowerCase()
    .replace(/\[[^\]]+\]/g, "")
    .replace(/[^a-z0-9]+/g, ".")
    .replace(/^\.+|\.+$/g, "")
    .slice(0, 120);

  return normalized || "app.log";
}

export function normalizeConsoleArgs(
  level: LogLevel,
  source: string,
  args: unknown[],
): NormalizedLogRecord {
  const fallback = `${source}.${level}`;
  const [firstArg, ...restArgs] = args;
  const body = normalizeLogBody(firstArg, fallback);
  const attributes: LogAttributes = {
    event: normalizeLogEvent(body),
    source,
  };

  if (firstArg instanceof Error) {
    Object.assign(attributes, normalizeError(firstArg));
  } else if (isPlainObject(firstArg)) {
    Object.assign(attributes, firstArg);
  } else if (firstArg !== undefined && typeof firstArg !== "string") {
    attributes.arg_0 = firstArg;
  }

  for (const [index, arg] of restArgs.entries()) {
    mergeLogArgument(attributes, arg, index + 1, body);
  }

  return {
    body,
    level,
    attributes: sanitizeLogAttributes(attributes),
  };
}

export function normalizeStructuredLog(
  level: LogLevel,
  source: string,
  body: string,
  attributes?: LogAttributes,
): NormalizedLogRecord {
  return {
    body: normalizeLogBody(body, `${source}.${level}`),
    level,
    attributes: sanitizeLogAttributes({
      event: normalizeLogEvent(body),
      source,
      ...attributes,
    }),
  };
}

export function durationSince(startedAt: number): number {
  const now =
    typeof performance !== "undefined" && typeof performance.now === "function"
      ? performance.now()
      : Date.now();

  return Math.max(0, Math.round((now - startedAt) * 100) / 100);
}

export function normalizeError(error: unknown): LogAttributes {
  if (error instanceof Error) {
    return {
      error_name: error.name,
      error_message: truncate(error.message, MAX_STRING_LENGTH),
      ...(error.stack
        ? { error_stack: truncate(error.stack, MAX_STACK_LENGTH) }
        : {}),
      ...readErrorLikeFields(error),
    };
  }

  if (isPlainObject(error)) {
    return {
      error_type: "object",
      ...readErrorLikeFields(error),
    };
  }

  return {
    error_type: typeof error,
    error_message: truncate(String(error), MAX_STRING_LENGTH),
  };
}

export function sanitizeLogAttributes(
  attributes: LogAttributes,
): Record<string, string | number | boolean> {
  const sanitized: Record<string, string | number | boolean> = {};
  const entries = Object.entries(attributes).slice(0, MAX_ATTRIBUTE_COUNT);

  for (const [rawKey, rawValue] of entries) {
    const key = normalizeAttributeKey(rawKey);
    if (!key || rawValue == null) continue;

    if (key === "error" || rawValue instanceof Error) {
      Object.assign(sanitized, sanitizeLogAttributes(normalizeError(rawValue)));
      continue;
    }

    if (isSensitiveKey(key)) {
      sanitized[key] = REDACTED;
      continue;
    }

    const value = sanitizeValue(rawValue);
    if (value != null) {
      sanitized[key] = value;
    }
  }

  if (Object.keys(attributes).length > MAX_ATTRIBUTE_COUNT) {
    sanitized.attributes_truncated = true;
  }

  return sanitized;
}

function mergeLogArgument(
  attributes: LogAttributes,
  arg: unknown,
  index: number,
  body: string,
) {
  if (arg instanceof Error) {
    Object.assign(attributes, normalizeError(arg));
    return;
  }

  if (isPlainObject(arg)) {
    Object.assign(attributes, arg);
    return;
  }

  attributes[`arg_${index}`] = bodyHasSensitiveContext(body) ? REDACTED : arg;
}

function readErrorLikeFields(error: object): LogAttributes {
  const record = error as Record<string, unknown>;
  const fields: LogAttributes = {};

  if (typeof record.name === "string") {
    fields.error_name = record.name;
  }

  if (typeof record.message === "string") {
    fields.error_message = truncate(record.message, MAX_STRING_LENGTH);
  }

  if (typeof record.description === "string" && fields.error_message == null) {
    fields.error_message = truncate(record.description, MAX_STRING_LENGTH);
  }

  if (typeof record.stack === "string") {
    fields.error_stack = truncate(record.stack, MAX_STACK_LENGTH);
  }

  for (const key of ["code", "status", "statusCode", "cause"]) {
    const value = record[key];
    if (value == null) continue;

    if (key === "cause" && value instanceof Error) {
      fields.error_cause = value.message;
    } else if (typeof value !== "object") {
      fields[`error_${normalizeAttributeKey(key)}`] = value;
    }
  }

  return fields;
}

function sanitizeValue(value: unknown): string | number | boolean | undefined {
  if (typeof value === "string") {
    return redactString(truncate(value, MAX_STRING_LENGTH));
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (value instanceof Error) {
    return truncate(value.message, MAX_STRING_LENGTH);
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (Array.isArray(value)) {
    return truncate(
      value
        .slice(0, 10)
        .map((item) =>
          typeof item === "object" ? "[object]" : String(sanitizeValue(item)),
        )
        .join(","),
      MAX_STRING_LENGTH,
    );
  }

  if (isPlainObject(value)) {
    return truncate(JSON.stringify(sanitizeObject(value)), MAX_STRING_LENGTH);
  }

  if (value == null) {
    return undefined;
  }

  return truncate(String(value), MAX_STRING_LENGTH);
}

function sanitizeObject(value: Record<string, unknown>) {
  const result: Record<string, string | number | boolean> = {};

  for (const [rawKey, rawValue] of Object.entries(value).slice(0, 20)) {
    const key = normalizeAttributeKey(rawKey);
    if (!key || rawValue == null) continue;
    result[key] = isSensitiveKey(key)
      ? REDACTED
      : (sanitizeValue(rawValue) ?? "");
  }

  if (Object.keys(value).length > 20) {
    result.object_truncated = true;
  }

  return result;
}

function normalizeAttributeKey(key: string): string {
  return key
    .trim()
    .replace(/[^a-zA-Z0-9_.-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
}

function isSensitiveKey(key: string): boolean {
  return (
    !allowedIdentityKeys.has(key) &&
    sensitiveKeyPattern.test(key.replace(/([a-z])([A-Z])/g, "$1_$2"))
  );
}

function bodyHasSensitiveContext(body: string): boolean {
  return sensitiveKeyPattern.test(body.replace(/\s+/g, "_"));
}

function redactString(value: string): string {
  return value
    .replace(/Bearer\s+[A-Za-z0-9._~-]+/gi, "Bearer [redacted]")
    .replace(/ph[cp]_[A-Za-z0-9_-]+/g, "[redacted_posthog_key]")
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "[redacted_email]")
    .replace(
      /https?:\/\/[^\s?#]+(?:\?[^\s#]*)?/gi,
      (match) => match.split("?", 1)[0],
    );
}

function truncate(value: string, maxLength: number): string {
  return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    Object.prototype.toString.call(value) === "[object Object]"
  );
}
